import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useAppBar } from '../../providers/AppBarProvider';
import { fetchOwner, handleError, formatCurrency } from '../../utils/lib';
import { useLocation, useNavigate } from 'react-router-dom';
import ViewPage from './ViewPage';
import { useSnackbar } from "../../providers/SnackBarProvider";
import ViewOwnerInfo from './ViewOwnerInfo';
import { useConfig } from '../../providers/ConfigProvider';
import BottomSheet from '../../layouts/BottomSheet';
import axios from 'axios';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AddTaskIcon from '@mui/icons-material/AddTask';
import PersonIcon from '@mui/icons-material/Person';
import AddPet from '../../assets/ic_add_pet_2.svg?react';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import useSellStore from '../../store/sellStore';
import useAgendaStore from '../../store/agendaStore';
import usePaymentDialog from '../../hooks/usePaymentDialog';
import useOwnerStore from '../../store/ownerStore';
import usePetStore from '../../store/petStore';
import { useTranslation } from 'react-i18next';

function ViewOwner() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setNavigation } = useNavigationHelperStore();
  const snackbar = useSnackbar();
  const [id, setId] = useState(location.state.id);
  const [owner, setOwner] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const {updateTitle} = useAppBar();
  const {config, isLoadingConfig} = useConfig();
  const { resetSell } = useSellStore();
  const { resetAgenda } = useAgendaStore();
  const { resetOwner } = useOwnerStore();
  const { resetPet } = usePetStore();
  const { t } = useTranslation();
  // Usar el hook de diálogo de pagos
  const {
    handleAddPayment,
    PaymentDialog
  } = usePaymentDialog(
    // Callback onSuccess para recargar los datos del owner
    async () => {
      const updatedOwner = await fetchOwner(id, false);
      setOwner(updatedOwner);
    },
    snackbar
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const owner = await fetchOwner(id, location.state?.updateLastView || false);
        setOwner(owner);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  //carga el title y el avatarHandler
  useEffect(() => {
    if (owner) {
      const subtitle = owner.phone 
        ? (owner.email ? `${owner.phone} | ${owner.email}` : owner.phone)
        : owner.email;
      updateTitle(owner.thumb_url || '', owner.name, subtitle, false, PersonIcon, handleAvatarClick);
    }
  }, [owner]);

  const handleViewDebtDetail = (owner) => {
    navigate('/main/view-owner-debts', { 
      state: { 
        id: owner.id,
        name: owner.name,
        thumb_url: owner.thumb_url
      } 
    });
  };

  const handleAvatarClick = () => {
    //console.log('Avatar clicked! Owner data:', owner);
    if (owner?.photo_url) {
      window.open(owner.photo_url, '_blank');
    }
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false
  });

  const handleNewPet = () => {
    resetPet();
    navigate('/main/edit-pet', { state: { owner: owner } });
  };

  const handleSell = () => {
    resetSell();
    setNavigation('/main/view-owner', { state: { id: owner.id, updateLastView: false } });
    navigate('/main/edit-sell', { state: { owner: owner } });
  };

  const handleAgenda = () => {
    resetAgenda();
    setNavigation('/main/view-owner', { state: { id: owner.id, updateLastView: false } });
    navigate('/main/edit-agenda', { state: { owner: owner } });
  };

  const handleEdit = () => {
    resetOwner();
    navigate('/main/edit-owner', { state: { updateOwnerFromLocation: owner } });
  };

  const handleDeleteOpen = () => {
    setDeleteDialog({
      open: true
    });
  };

  const handleDeleteClose = () => {
    setDeleteDialog({
      open: false
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}owners?id=${owner.id}`,
        { withCredentials: true }
      );
      
      handleDeleteClose();
      navigate(-1);
    } catch (error) {
      handleError(error, snackbar);
    }
  };

  if (isLoading || isLoadingConfig || !owner) {
    return <ViewPage isLoading={true} />;
  }

  return (
    <ViewPage 
      isLoading={isLoading || isLoadingConfig}
    >
      <Box sx={{ px: 1 }}>
        <ViewOwnerInfo
          owner={owner}
          onViewDebtDetail={handleViewDebtDetail}
          onAddPayment={handleAddPayment}
          petNaming={config.vet.pet_naming_es_plural}
        />
      </Box>

      {PaymentDialog}

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('view.confirmDeleteOwnerTitle', { name: owner?.name })}</DialogTitle>
        <DialogContent>
          {t('view.confirmDeleteOwnerMessage', { name: owner?.name })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <BottomSheet 
        zIndex={1001}
        buttonGroups={[
          {
            label: t('common.actions'),
            buttons: [
              ...(config.permissions.actions_permissions.pets.create ? [{
                label: t('main.addPet', { name: config.vet.pet_naming_es }),
                action: handleNewPet,
                icon: <AddPet width={42} height={42} />
              }] : []),
              ...(config.permissions.actions_permissions.sells.create ? [{
                label: t('main.sell'),
                action: handleSell,
                icon: <PointOfSaleIcon />
              }] : []),
              ...(config.permissions.actions_permissions.agenda.create ? [{
                label: t('main.agenda'),
                action: handleAgenda,
                icon: <AddTaskIcon />
              }] : []),
            ].filter(Boolean)
          },
          {
            label: config.vet.owner_naming_es,
            buttons: [
              ...(config.permissions.actions_permissions.owners.update ? [{
                label: t('view.vetActionEdit'),
                action: handleEdit,
                icon: <EditIcon />
              }] : []),
              ...(config.permissions.actions_permissions.owners.delete ? [{
                label: t('common.delete'),
                action: handleDeleteOpen,
                icon: <DeleteIcon />
              }] : []),
            ].filter(Boolean)
          },
        ]}
      />

    </ViewPage>
  );
}

export default ViewOwner;

